import type {
  MyStatsData,
  RankingsListData,
  RankingDetail,
  RankingAutoDraft,
  ScoringRules,
  RankingRoundDetail,
  RankingCreateResponse,
  RankingJoinResponse,
  RankingInvitePreview,
  WildcardCandidate,
  WildcardPriority,
  PlayerHistoryData,
  RankingPartialStandingsData,
  RankingBonus,
  RankingDashboardData,
  RankingTimelineData,
  RankingEvolutionVideoStatus,
  CompetitionsListData,
  CompetitionDetail,
  CompetitionGroupsData,
  CompetitionBracketData,
  CompetitionInvitePreview,
  CompetitionCreateResponse,
  CompetitionJoinResponse,
  User,
  Subscriber,
  MyInvoice,
  AsaasStatus,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const IS_MOCK_AUTH = import.meta.env.VITE_MOCK_AUTH === 'true';
// Mock mode used to model free-vs-paid; the only account state left that
// changes the UI is whether a LetzPlay profile has been imported.
const IS_MOCK_LETZPLAY_CONNECTED = import.meta.env.VITE_MOCK_LETZPLAY_CONNECTED !== 'false';

// ============================================================================
// Base Types
// ============================================================================

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

// Mock data for development
const MOCK_SUBSCRIBER_BASE = {
  id: '58c988a7-0709-4d24-8e76-317236d80230',
  email: 'yagorp15+test01@gmail.com',
  name: 'Yago Pessoa',
  avatarUrl: 'https://res.cloudinary.com/lptennis/image/upload/c_fit,h_250,q_auto:low,w_250/v1769459517/gzmlchojq0gjihpkcccm.webp?_a=BACAGSEv',
  userId: null,
  letzplayUsername: IS_MOCK_LETZPLAY_CONNECTED ? 'yagopessoa' : null,
  lastSyncAt: '2026-02-03T20:25:39.154+00:00',
  createdAt: '2026-02-03T20:21:57.050419+00:00',
};

const MOCK_RESPONSES: Record<string, unknown> = {
  // Mock GETs that find no entry warn and fall through to the network; push is
  // already disabled in mock mode by usePushNotifications, so this is only here
  // so the probe never produces a confusing console warning.
  '/api/vision/push/public-key': { publicKey: 'mock-vapid-public-key' },
  '/api/auth/me': {
    user: {
      id: '25981aba-badd-456a-b470-c718bb1f6de6',
      email: 'yagorp15+test01@gmail.com',
      name: null,
      avatarUrl: null,
    },
    subscriber: {
      ...MOCK_SUBSCRIBER_BASE,
      pendingRankingMatches: [],
      wildcardNeededRankings: [],
    },
  },
  '/api/vision/me/stats': {
    totals: {
      roundsPlayed: 9,
      matchesPlayed: 27,
      matchesWon: 17,
      matchesLost: 10,
      winRate: 62.96,
      roundsByWins: [
        { wins: 3, rounds: 2 },
        { wins: 2, rounds: 4 },
        { wins: 1, rounds: 2 },
        { wins: 0, rounds: 1 },
      ],
    },
    partners: [
      { subscriberId: 'mock-partner-1', name: 'Maria Silva', avatarUrl: null, matches: 6, wins: 5, losses: 1, winRate: 83 },
      { subscriberId: 'mock-partner-2', name: 'João Santos', avatarUrl: null, matches: 5, wins: 3, losses: 2, winRate: 60 },
      { subscriberId: 'mock-partner-3', name: 'Ana Costa', avatarUrl: null, matches: 4, wins: 2, losses: 2, winRate: 50 },
      { subscriberId: 'mock-partner-4', name: 'Pedro Lima', avatarUrl: null, matches: 4, wins: 3, losses: 1, winRate: 75 },
      { subscriberId: 'mock-partner-5', name: 'Carla Mendes', avatarUrl: null, matches: 3, wins: 1, losses: 2, winRate: 33 },
      { subscriberId: 'mock-partner-6', name: 'Lucas Oliveira', avatarUrl: null, matches: 3, wins: 2, losses: 1, winRate: 67 },
      { subscriberId: 'mock-partner-7', name: 'Fernanda Rocha', avatarUrl: null, matches: 3, wins: 3, losses: 0, winRate: 100 },
      { subscriberId: 'mock-partner-8', name: 'Bruno Alves', avatarUrl: null, matches: 2, wins: 1, losses: 1, winRate: 50 },
      { subscriberId: 'mock-partner-9', name: 'Juliana Ferreira', avatarUrl: null, matches: 2, wins: 2, losses: 0, winRate: 100 },
      { subscriberId: 'mock-partner-10', name: 'Ricardo Martins', avatarUrl: null, matches: 2, wins: 0, losses: 2, winRate: 0 },
      { subscriberId: 'mock-partner-11', name: 'Patrícia Souza', avatarUrl: null, matches: 1, wins: 1, losses: 0, winRate: 100 },
      { subscriberId: 'mock-partner-12', name: 'Gabriel Torres', avatarUrl: null, matches: 1, wins: 0, losses: 1, winRate: 0 },
    ],
    opponents: [
      { subscriberId: 'mock-opponent-1', name: 'Pedro Lima', avatarUrl: null, matches: 9, wins: 6, losses: 3, winRate: 67 },
      { subscriberId: 'mock-opponent-2', name: 'Carla Mendes', avatarUrl: null, matches: 7, wins: 3, losses: 4, winRate: 43 },
      { subscriberId: 'mock-opponent-3', name: 'Lucas Oliveira', avatarUrl: null, matches: 5, wins: 4, losses: 1, winRate: 80 },
      { subscriberId: 'mock-opponent-4', name: 'Diego Ferreira', avatarUrl: null, matches: 5, wins: 2, losses: 3, winRate: 40 },
      { subscriberId: 'mock-opponent-5', name: 'Renata Oliveira', avatarUrl: null, matches: 4, wins: 3, losses: 1, winRate: 75 },
      { subscriberId: 'mock-opponent-6', name: 'Marcos Paulo', avatarUrl: null, matches: 4, wins: 1, losses: 3, winRate: 25 },
      { subscriberId: 'mock-opponent-7', name: 'Thiago Mendes', avatarUrl: null, matches: 4, wins: 2, losses: 2, winRate: 50 },
      { subscriberId: 'mock-opponent-8', name: 'Amanda Silva', avatarUrl: null, matches: 3, wins: 3, losses: 0, winRate: 100 },
      { subscriberId: 'mock-opponent-9', name: 'Rafael Costa', avatarUrl: null, matches: 3, wins: 1, losses: 2, winRate: 33 },
      { subscriberId: 'mock-opponent-10', name: 'Camila Santos', avatarUrl: null, matches: 2, wins: 1, losses: 1, winRate: 50 },
      { subscriberId: 'mock-opponent-11', name: 'Felipe Andrade', avatarUrl: null, matches: 2, wins: 2, losses: 0, winRate: 100 },
      { subscriberId: 'mock-opponent-12', name: 'Eduardo Costa', avatarUrl: null, matches: 2, wins: 0, losses: 2, winRate: 0 },
      { subscriberId: 'mock-opponent-13', name: 'Bruno Alves', avatarUrl: null, matches: 1, wins: 1, losses: 0, winRate: 100 },
      { subscriberId: 'mock-opponent-14', name: 'Juliana Ferreira', avatarUrl: null, matches: 1, wins: 0, losses: 1, winRate: 0 },
    ],
    evolution: [
      { month: '2025-09', matches: 6, wins: 2, winRate: 33 },
      { month: '2025-10', matches: 8, wins: 4, winRate: 50 },
      { month: '2025-11', matches: 5, wins: 2, winRate: 40 },
      { month: '2025-12', matches: 9, wins: 6, winRate: 67 },
      { month: '2026-01', matches: 11, wins: 6, winRate: 55 },
      { month: '2026-02', matches: 7, wins: 5, winRate: 71 },
      { month: '2026-03', matches: 10, wins: 6, winRate: 60 },
      { month: '2026-04', matches: 6, wins: 3, winRate: 50 },
      { month: '2026-05', matches: 9, wins: 6, winRate: 67 },
      { month: '2026-06', matches: 12, wins: 8, winRate: 67 },
      { month: '2026-07', matches: 8, wins: 7, winRate: 88 },
      { month: '2026-08', matches: 5, wins: 3, winRate: 60 },
    ],
  },

  // Rankings list — drives the home screen and /rankings
  '/api/vision/rankings': {
    rankings: [
      {
        id: 'mock-ranking-1',
        groupId: 'mock-group-1',
        name: 'Panela Beach',
        imageUrl: null,
        overlayUrl: null,
        subtitle: 'Temporada 1',
        description: null,
        inviteCode: 'PANELA01',
        status: 'active',
        finishedAt: null,
        playerCount: 11,
        activePlayerCount: 11,
        myPosition: 2,
        myPoints: 1585,
        signupsOpen: true,
        myRole: 'admin',
        myStatus: 'inactive',
        currentRoundNumber: 7,
        totalRounds: 12,
        discardRounds: 2,
        pendingPlayerCount: 2,
        seasonCount: 1,
        paymentEnabled: true,
        paymentAmountCents: 3000,
        createdAt: '2026-02-01T12:00:00Z',
      },
      {
        id: 'mock-ranking-2',
        groupId: 'mock-group-2',
        name: 'BT Friends',
        imageUrl: null,
        overlayUrl: null,
        subtitle: 'Temporada 2',
        description: null,
        inviteCode: 'FRIENDS2',
        status: 'active',
        finishedAt: null,
        playerCount: 8,
        activePlayerCount: 8,
        myPosition: 5,
        myPoints: 720,
        signupsOpen: false,
        myRole: 'player',
        myStatus: 'pending_payment',
        currentRoundNumber: 3,
        totalRounds: null,
        discardRounds: 0,
        pendingPlayerCount: 0,
        seasonCount: 2,
        paymentEnabled: true,
        paymentAmountCents: 2500,
        createdAt: '2026-05-01T12:00:00Z',
      },
    ],
  },

  // ---- Competitions ("Torneios") mocks ----
  '/api/vision/competitions': {
    competitions: [
      {
        id: 'mock-comp-1',
        name: 'Copa de Verão',
        description: 'Torneio de duplas sorteadas',
        inviteCode: 'TORNEIO1',
        status: 'knockout',
        requiresApproval: false,
        playerCount: 8,
        myRole: 'admin',
        isCreator: true,
        createdAt: '2026-05-01T12:00:00Z',
      },
      {
        id: 'mock-comp-2',
        name: 'Rachão de Sábado',
        description: null,
        inviteCode: 'TORNEIO2',
        status: 'registration',
        requiresApproval: true,
        playerCount: 5,
        myRole: 'player',
        isCreator: false,
        createdAt: '2026-05-20T12:00:00Z',
      },
    ],
  },
  '/api/vision/competitions/mock-comp-1': {
    id: 'mock-comp-1',
    name: 'Copa de Verão',
    description: 'Torneio de duplas sorteadas',
    imageUrl: null,
    inviteCode: 'TORNEIO1',
    status: 'knockout',
    requiresApproval: false,
    teamsPerGroup: 3,
    teamsAdvancingPerGroup: 2,
    maxPlayers: null,
    thirdPlaceMatch: true,
    poolCount: null,
    isCreator: true,
    myRole: 'admin',
    myStatus: 'active',
    myPlayerId: 'mp1',
    players: [
      { id: 'mp1', subscriberId: MOCK_SUBSCRIBER_BASE.id, name: 'Yago Pessoa', avatarUrl: MOCK_SUBSCRIBER_BASE.avatarUrl, status: 'active', role: 'admin', teamId: 'mt1', joinedAt: '2026-05-01T12:00:00Z' },
      { id: 'mp2', subscriberId: 'sub-2', name: 'Maria Silva', avatarUrl: null, status: 'active', role: 'player', teamId: 'mt1', joinedAt: '2026-05-01T12:01:00Z' },
      { id: 'mp3', subscriberId: 'sub-3', name: 'João Costa', avatarUrl: null, status: 'active', role: 'player', teamId: 'mt2', joinedAt: '2026-05-01T12:02:00Z' },
      { id: 'mp4', subscriberId: 'sub-4', name: 'Ana Lima', avatarUrl: null, status: 'active', role: 'player', teamId: 'mt2', joinedAt: '2026-05-01T12:03:00Z' },
    ],
    teams: [
      { id: 'mt1', teamNumber: 1, players: [{ name: 'Yago Pessoa', avatarUrl: null }, { name: 'Maria Silva', avatarUrl: null }], groupRank: 1, seed: 1, matchesPlayed: 2, wins: 2, losses: 0, gamesWon: 12, gamesLost: 6, finalPosition: 1 },
      { id: 'mt2', teamNumber: 2, players: [{ name: 'João Costa', avatarUrl: null }, { name: 'Ana Lima', avatarUrl: null }], groupRank: 2, seed: 2, matchesPlayed: 2, wins: 1, losses: 1, gamesWon: 9, gamesLost: 8, finalPosition: 2 },
    ],
    groupStageStartedAt: '2026-05-02T12:00:00Z',
    knockoutStartedAt: '2026-05-05T12:00:00Z',
    finishedAt: null,
    createdAt: '2026-05-01T12:00:00Z',
  },
  '/api/vision/competitions/mock-comp-1/groups': {
    isAdmin: true,
    canEditGroupResults: false,
    groups: [
      {
        id: 'mg1',
        groupNumber: 1,
        teams: [
          { id: 'mt1', teamNumber: 1, players: [{ name: 'Yago Pessoa', avatarUrl: null }, { name: 'Maria Silva', avatarUrl: null }], groupRank: 1, seed: 1, matchesPlayed: 2, wins: 2, losses: 0, gamesWon: 12, gamesLost: 6, finalPosition: null },
          { id: 'mt2', teamNumber: 2, players: [{ name: 'João Costa', avatarUrl: null }, { name: 'Ana Lima', avatarUrl: null }], groupRank: 2, seed: 2, matchesPlayed: 2, wins: 1, losses: 1, gamesWon: 9, gamesLost: 8, finalPosition: null },
          { id: 'mt3', teamNumber: 3, players: [{ name: 'Pedro Reis', avatarUrl: null }, { name: 'Lia Souza', avatarUrl: null }], groupRank: 3, seed: null, matchesPlayed: 2, wins: 0, losses: 2, gamesWon: 5, gamesLost: 12, finalPosition: null },
        ],
        matches: [
          { id: 'gm1', phase: 'group', groupId: 'mg1', matchNumber: 1, bracketRound: null, bracketPosition: null, nextMatchId: null, nextMatchSlot: null, team1: { id: 'mt1', teamNumber: 1, players: [{ name: 'Yago Pessoa', avatarUrl: null }, { name: 'Maria Silva', avatarUrl: null }], groupRank: 1, seed: 1, matchesPlayed: 2, wins: 2, losses: 0, gamesWon: 12, gamesLost: 6, finalPosition: null }, team2: { id: 'mt2', teamNumber: 2, players: [{ name: 'João Costa', avatarUrl: null }, { name: 'Ana Lima', avatarUrl: null }], groupRank: 2, seed: 2, matchesPlayed: 2, wins: 1, losses: 1, gamesWon: 9, gamesLost: 8, finalPosition: null }, team1Games: 6, team2Games: 3, tiebreakScore: null, winnerTeamId: 'mt1', isWo: false, status: 'completed' },
          { id: 'gm2', phase: 'group', groupId: 'mg1', matchNumber: 2, bracketRound: null, bracketPosition: null, nextMatchId: null, nextMatchSlot: null, team1: { id: 'mt1', teamNumber: 1, players: [{ name: 'Yago Pessoa', avatarUrl: null }, { name: 'Maria Silva', avatarUrl: null }], groupRank: 1, seed: 1, matchesPlayed: 2, wins: 2, losses: 0, gamesWon: 12, gamesLost: 6, finalPosition: null }, team2: { id: 'mt3', teamNumber: 3, players: [{ name: 'Pedro Reis', avatarUrl: null }, { name: 'Lia Souza', avatarUrl: null }], groupRank: 3, seed: null, matchesPlayed: 2, wins: 0, losses: 2, gamesWon: 5, gamesLost: 12, finalPosition: null }, team1Games: 6, team2Games: 1, tiebreakScore: null, winnerTeamId: 'mt1', isWo: false, status: 'completed' },
          { id: 'gm3', phase: 'group', groupId: 'mg1', matchNumber: 3, bracketRound: null, bracketPosition: null, nextMatchId: null, nextMatchSlot: null, team1: { id: 'mt2', teamNumber: 2, players: [{ name: 'João Costa', avatarUrl: null }, { name: 'Ana Lima', avatarUrl: null }], groupRank: 2, seed: 2, matchesPlayed: 2, wins: 1, losses: 1, gamesWon: 9, gamesLost: 8, finalPosition: null }, team2: { id: 'mt3', teamNumber: 3, players: [{ name: 'Pedro Reis', avatarUrl: null }, { name: 'Lia Souza', avatarUrl: null }], groupRank: 3, seed: null, matchesPlayed: 2, wins: 0, losses: 2, gamesWon: 5, gamesLost: 12, finalPosition: null }, team1Games: 6, team2Games: 4, tiebreakScore: null, winnerTeamId: 'mt2', isWo: false, status: 'completed' },
        ],
      },
    ],
  },
  '/api/vision/competitions/mock-comp-1/bracket': {
    isAdmin: true,
    champion: { id: 'mt1', teamNumber: 1, players: [{ name: 'Yago Pessoa', avatarUrl: null }, { name: 'Maria Silva', avatarUrl: null }], groupRank: 1, seed: 1, matchesPlayed: 2, wins: 2, losses: 0, gamesWon: 12, gamesLost: 6, finalPosition: 1 },
    rounds: [
      {
        round: 1,
        matches: [
          { id: 'km1', phase: 'knockout', groupId: null, matchNumber: null, bracketRound: 1, bracketPosition: 1, nextMatchId: 'kfinal', nextMatchSlot: 1, gameNumber: 1, team1: { id: 'mt1', teamNumber: 1, players: [{ name: 'Yago Pessoa', avatarUrl: null }, { name: 'Maria Silva', avatarUrl: null }], groupRank: 1, seed: 1, matchesPlayed: 2, wins: 2, losses: 0, gamesWon: 12, gamesLost: 6, finalPosition: 1 }, team2: { id: 'mt4', teamNumber: 4, players: [{ name: 'Bruno Dias', avatarUrl: null }, { name: 'Carla Melo', avatarUrl: null }], groupRank: 2, seed: 4, matchesPlayed: 0, wins: 0, losses: 0, gamesWon: 0, gamesLost: 0, finalPosition: null }, team1Games: 6, team2Games: 2, tiebreakScore: null, winnerTeamId: 'mt1', isWo: false, status: 'completed' },
          { id: 'km2', phase: 'knockout', groupId: null, matchNumber: null, bracketRound: 1, bracketPosition: 2, nextMatchId: 'kfinal', nextMatchSlot: 2, gameNumber: 2, team1: { id: 'mt3', teamNumber: 3, players: [{ name: 'Pedro Reis', avatarUrl: null }, { name: 'Lia Souza', avatarUrl: null }], groupRank: 1, seed: 3, matchesPlayed: 0, wins: 0, losses: 0, gamesWon: 0, gamesLost: 0, finalPosition: null }, team2: { id: 'mt2', teamNumber: 2, players: [{ name: 'João Costa', avatarUrl: null }, { name: 'Ana Lima', avatarUrl: null }], groupRank: 2, seed: 2, matchesPlayed: 2, wins: 1, losses: 1, gamesWon: 9, gamesLost: 8, finalPosition: 2 }, team1Games: 4, team2Games: 6, tiebreakScore: null, winnerTeamId: 'mt2', isWo: false, status: 'completed' },
        ],
      },
      {
        round: 2,
        matches: [
          { id: 'kfinal', phase: 'knockout', groupId: null, matchNumber: null, bracketRound: 2, bracketPosition: 1, nextMatchId: null, nextMatchSlot: null, team1: { id: 'mt1', teamNumber: 1, players: [{ name: 'Yago Pessoa', avatarUrl: null }, { name: 'Maria Silva', avatarUrl: null }], groupRank: 1, seed: 1, matchesPlayed: 2, wins: 2, losses: 0, gamesWon: 12, gamesLost: 6, finalPosition: 1 }, team2: { id: 'mt2', teamNumber: 2, players: [{ name: 'João Costa', avatarUrl: null }, { name: 'Ana Lima', avatarUrl: null }], groupRank: 2, seed: 2, matchesPlayed: 2, wins: 1, losses: 1, gamesWon: 9, gamesLost: 8, finalPosition: 2 }, team1Games: 7, team2Games: 6, tiebreakScore: 5, winnerTeamId: 'mt1', isWo: false, status: 'completed' },
        ],
      },
    ],
  },
  '/api/vision/competitions/invite/TORNEIO1': {
    id: 'mock-comp-1',
    name: 'Copa de Verão',
    description: 'Torneio de duplas sorteadas',
    playerCount: 8,
    status: 'registration',
    requiresApproval: false,
    isMember: false,
  },
};

async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  // Mock mode: return fake data without hitting the backend
  if (IS_MOCK_AUTH) {
    console.log('[API Mock] Intercepting request:', endpoint);

    // Extract base endpoint without query params for mock lookup
    const baseEndpoint = endpoint.split('?')[0];

    const mockData = MOCK_RESPONSES[baseEndpoint];

    if (mockData) {
      console.log('[API Mock] Returning mocked data for:', baseEndpoint);
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true, data: mockData as T };
    }

    // For POST endpoints (connect, etc), just return success
    if (options.method === 'POST') {
      console.log('[API Mock] Returning success for POST:', baseEndpoint);
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true, data: { success: true } as T };
    }

    console.warn('[API Mock] No mock data found for:', endpoint);
  }

  // Real API mode
  const token = localStorage.getItem('sb-access-token');

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    const json = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: json.error || { code: 'UNKNOWN', message: 'Something went wrong' },
      };
    }

    // Backend returns { success: true, data: {...} }, so extract the actual data
    // Some endpoints return data directly, others wrap in { success, data }
    const data = json.data !== undefined ? json.data : json;

    return { success: true, data };
  } catch {
    return {
      success: false,
      error: { code: 'NETWORK_ERROR', message: 'Failed to connect to server' },
    };
  }
}

export const api = {
  get: <T>(endpoint: string) => fetchWithAuth<T>(endpoint, { method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown) =>
    fetchWithAuth<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),

  put: <T>(endpoint: string, body?: unknown) =>
    fetchWithAuth<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body ?? {}),
    }),

  patch: <T>(endpoint: string, body?: unknown) =>
    fetchWithAuth<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body ?? {}),
    }),

  // Auth
  auth: {
    me: () => api.get<{ user: User; subscriber: Subscriber }>('/api/auth/me'),
    updateProfile: (data: { name?: string; cpf?: string; birthDate?: string }) =>
      api.patch<{ subscriber: Subscriber }>('/api/auth/profile', data),
    recordSignupIntent: (data: { intent: string; cta: string; clickedAt?: string }) =>
      api.post<{ recorded: boolean }>('/api/auth/signup-intent', data),
    uploadAvatar: (image: string) =>
      api.post<{ subscriber: Subscriber }>('/api/auth/avatar', { image }),
  },

  // Asaas connection (paid rankings)
  asaas: {
    connect: (apiKey: string) =>
      api.post<{ status: AsaasStatus }>('/api/vision/asaas/connect', { apiKey }),
    status: () => api.get<{ status: AsaasStatus }>('/api/vision/asaas/status'),
    disconnect: () =>
      fetchWithAuth<{ success: boolean }>('/api/vision/asaas', { method: 'DELETE' }),
  },

  // Vision
  vision: {
    previewProfile: (username: string) =>
      api.post<ProfilePreviewResponse>('/api/vision/preview-profile', { letzplayUsername: username }),
    connect: (username: string) =>
      api.post<{ success: boolean }>('/api/vision/connect', { letzplayUsername: username }),
    submitSupport: (message: string) =>
      api.post<{ success: boolean }>('/api/vision/support', { message }),
    getSupportRequests: () =>
      api.get<{ requests: Array<{ id: string; message: string; created_at: string }> }>('/api/vision/support'),
    getFeedbackStatus: (feature: string) =>
      api.get<{ submitted: boolean }>(`/api/vision/feedback/status?feature=${encodeURIComponent(feature)}`),
    submitFeedback: (feature: string, rating: string | null, comment: string | null) =>
      api.post<{ success: boolean }>('/api/vision/feedback', { feature, rating, comment }),
    // Emails the signed-in user the btvision.app link, so they can escape an
    // install on the old origin. iOS PWAs cannot open Safari themselves.
    sendMigrationLink: () =>
      api.post<{ success: boolean; email: string }>('/api/vision/migration-link'),

    // Web Push. `getPublicKey` doubles as the feature probe: it answers 503
    // PUSH_DISABLED when the backend has no VAPID keypair, which is how the
    // Settings card learns to hide itself.
    push: {
      getPublicKey: () => api.get<{ publicKey: string }>('/api/vision/push/public-key'),
      subscribe: (body: {
        subscription: PushSubscriptionJSON;
        userAgent?: string;
        appHost?: string;
      }) => api.post<{ success: boolean }>('/api/vision/push/subscribe', body),
      unsubscribe: (body: { endpoint: string }) =>
        api.post<{ success: boolean }>('/api/vision/push/unsubscribe', body),
      sendTest: () =>
        api.post<{ sent: number; failed: number }>('/api/vision/push/test'),
    },
  },

  // Personal stats (across every ranking)
  stats: {
    me: () => api.get<MyStatsData>('/api/vision/me/stats'),
  },

  // Rankings
  rankings: {
    list: () => api.get<RankingsListData>('/api/vision/rankings'),
    create: (data: {
      name: string;
      description?: string;
      scoringRules?: Partial<ScoringRules>;
      totalRounds?: number | null;
      discardRounds?: number;
      payment?: { enabled: boolean; amountCents?: number; dueDays?: number };
    }) => api.post<RankingCreateResponse>('/api/vision/rankings', data),
    detail: (id: string) => api.get<RankingDetail>(`/api/vision/rankings/${id}`),
    update: (id: string, data: Record<string, unknown>) =>
      api.put<RankingDetail>(`/api/vision/rankings/${id}`, data),
    finish: (id: string) => api.post(`/api/vision/rankings/${id}/finish`),
    reactivate: (id: string) => api.post(`/api/vision/rankings/${id}/reactivate`),
    skipAutoDraft: (id: string) =>
      api.post<{ autoDraft: RankingAutoDraft | null }>(`/api/vision/rankings/${id}/auto-draft/skip`),
    newSeason: (id: string, data?: { name?: string; description?: string }) =>
      api.post<RankingCreateResponse>(`/api/vision/rankings/${id}/new-season`, data),
    delete: (id: string) =>
      fetchWithAuth<{ message: string }>(`/api/vision/rankings/${id}`, { method: 'DELETE' }),
    invitePreview: (code: string) =>
      api.get<RankingInvitePreview>(`/api/vision/rankings/invite/${code}`),
    join: (code: string) =>
      api.post<RankingJoinResponse>(`/api/vision/rankings/join/${code}`),
    myInvoice: (rankingId: string) =>
      api.get<{ invoice: MyInvoice | null; myStatus: string }>(`/api/vision/rankings/${rankingId}/my-invoice`),
    markPaid: (rankingId: string, playerId: string) =>
      api.post(`/api/vision/rankings/${rankingId}/players/${playerId}/mark-paid`),
    exemptPlayer: (rankingId: string, playerId: string) =>
      api.post(`/api/vision/rankings/${rankingId}/players/${playerId}/exempt`),
    approvePlayer: (rankingId: string, playerId: string) =>
      api.post(`/api/vision/rankings/${rankingId}/players/${playerId}/approve`),
    rejectPlayer: (rankingId: string, playerId: string) =>
      api.post(`/api/vision/rankings/${rankingId}/players/${playerId}/reject`),
    togglePlayer: (rankingId: string, playerId: string) =>
      api.post(`/api/vision/rankings/${rankingId}/players/${playerId}/toggle`),
    removePlayer: (rankingId: string, playerId: string) =>
      api.post(`/api/vision/rankings/${rankingId}/players/${playerId}/remove`),
    promotePlayer: (rankingId: string, playerId: string) =>
      api.post(`/api/vision/rankings/${rankingId}/players/${playerId}/promote`),
    demotePlayer: (rankingId: string, playerId: string) =>
      api.post(`/api/vision/rankings/${rankingId}/players/${playerId}/demote`),
    draftRound: (rankingId: string) =>
      api.post<RankingRoundDetail>(`/api/vision/rankings/${rankingId}/rounds/draft`),
    startRound: (rankingId: string, roundId: string) =>
      api.post(`/api/vision/rankings/${rankingId}/rounds/${roundId}/start`),
    completeRound: (rankingId: string, roundId: string) =>
      api.post(`/api/vision/rankings/${rankingId}/rounds/${roundId}/complete`),
    deleteRound: (rankingId: string, roundId: string) =>
      fetchWithAuth(`/api/vision/rankings/${rankingId}/rounds/${roundId}`, { method: 'DELETE' }),
    roundDetail: (rankingId: string, roundId: string) =>
      api.get<RankingRoundDetail>(`/api/vision/rankings/${rankingId}/rounds/${roundId}`),
    wildcardCandidates: (rankingId: string, roundId: string, groupId?: string) =>
      api.get<WildcardCandidate[]>(`/api/vision/rankings/${rankingId}/rounds/${roundId}/wildcard-candidates${groupId ? `?groupId=${groupId}` : ''}`),
    wildcardPriority: (rankingId: string, roundId: string) =>
      api.get<WildcardPriority>(`/api/vision/rankings/${rankingId}/rounds/${roundId}/wildcard-priority`),
    assignWildcard: (rankingId: string, roundId: string, groupId: string, data: { playerId: string; slotPosition: 2 | 3 | 4 }) =>
      api.post(`/api/vision/rankings/${rankingId}/rounds/${roundId}/groups/${groupId}/assign-wildcard`, data),
    swapPlayer: (rankingId: string, roundId: string, groupId: string, data: { slotPosition: 1 | 2 | 3 | 4; newPlayerId: string }) =>
      api.post(`/api/vision/rankings/${rankingId}/rounds/${roundId}/groups/${groupId}/swap-player`, data),
    markGroupPlayed: (rankingId: string, roundId: string, groupId: string, data: { countsAsPlayed: boolean }) =>
      api.post(`/api/vision/rankings/${rankingId}/rounds/${roundId}/groups/${groupId}/mark-played`, data),
    submitMatch: (rankingId: string, matchId: string, data: { team1Games: number; team2Games: number; tiebreakScore?: number | null }) =>
      api.post(`/api/vision/rankings/${rankingId}/matches/${matchId}/submit`, data),
    disputeMatch: (rankingId: string, matchId: string) =>
      api.post(`/api/vision/rankings/${rankingId}/matches/${matchId}/dispute`),
    overrideMatch: (rankingId: string, matchId: string, data: { team1Games: number; team2Games: number; tiebreakScore?: number | null }) =>
      api.post(`/api/vision/rankings/${rankingId}/matches/${matchId}/override`, data),
    woMatch: (rankingId: string, matchId: string, data: { absentPlayerIds: string[]; applyPenalty: boolean }) =>
      api.post(`/api/vision/rankings/${rankingId}/matches/${matchId}/wo`, data),
    playerHistory: (rankingId: string, playerId: string) =>
      api.get<PlayerHistoryData>(`/api/vision/rankings/${rankingId}/players/${playerId}/history`),
    partialStandings: (rankingId: string) =>
      api.get<RankingPartialStandingsData>(`/api/vision/rankings/${rankingId}/standings/partial`),
    bonuses: (rankingId: string) =>
      api.get<RankingBonus[]>(`/api/vision/rankings/${rankingId}/bonuses`),
    addBonus: (rankingId: string, data: { playerId: string; points: number; date: string; reason?: string | null }) =>
      api.post<RankingBonus>(`/api/vision/rankings/${rankingId}/bonuses`, data),
    updateBonus: (rankingId: string, bonusId: string, data: { playerId?: string; points?: number; date?: string; reason?: string | null }) =>
      api.put<RankingBonus>(`/api/vision/rankings/${rankingId}/bonuses/${bonusId}`, data),
    deleteBonus: (rankingId: string, bonusId: string) =>
      fetchWithAuth<{ message: string }>(`/api/vision/rankings/${rankingId}/bonuses/${bonusId}`, { method: 'DELETE' }),
    dashboard: (rankingId: string) =>
      api.get<RankingDashboardData>(`/api/vision/rankings/${rankingId}/dashboard`),
    timeline: (rankingId: string) =>
      api.get<RankingTimelineData>(`/api/vision/rankings/${rankingId}/timeline`),
    evolutionVideoStatus: (rankingId: string) =>
      api.get<RankingEvolutionVideoStatus>(`/api/vision/rankings/${rankingId}/evolution-video`),
    requestEvolutionVideo: (rankingId: string) =>
      api.post<RankingEvolutionVideoStatus>(`/api/vision/rankings/${rankingId}/evolution-video`),
    uploadGroupImage: (rankingId: string, base64Image: string) =>
      api.post<{ imageUrl: string }>(`/api/vision/rankings/${rankingId}/group-image`, { image: base64Image }),
    deleteGroupImage: (rankingId: string) =>
      fetchWithAuth<{ message: string }>(`/api/vision/rankings/${rankingId}/group-image`, { method: 'DELETE' }),
    uploadOverlay: (rankingId: string, base64Image: string) =>
      api.post<{ overlayUrl: string }>(`/api/vision/rankings/${rankingId}/overlay`, { image: base64Image }),
    deleteOverlay: (rankingId: string) =>
      fetchWithAuth<{ message: string }>(`/api/vision/rankings/${rankingId}/overlay`, { method: 'DELETE' }),
  },

  // Competitions ("Torneios")
  competitions: {
    list: () => api.get<CompetitionsListData>('/api/vision/competitions'),
    create: (data: {
      name: string;
      description?: string;
      requiresApproval?: boolean;
      teamsPerGroup?: number;
      teamsAdvancingPerGroup?: number;
      maxPlayers?: number | null;
      thirdPlaceMatch?: boolean;
      poolCount?: number | null;
      creatorPlays?: boolean;
    }) => api.post<CompetitionCreateResponse>('/api/vision/competitions', data),
    detail: (id: string) => api.get<CompetitionDetail>(`/api/vision/competitions/${id}`),
    update: (id: string, data: Record<string, unknown>) =>
      api.put<{ id: string }>(`/api/vision/competitions/${id}`, data),
    uploadImage: (id: string, base64Image: string) =>
      api.post<{ imageUrl: string }>(`/api/vision/competitions/${id}/image`, { image: base64Image }),
    deleteImage: (id: string) =>
      fetchWithAuth<{ message: string }>(`/api/vision/competitions/${id}/image`, { method: 'DELETE' }),
    invitePreview: (code: string) =>
      api.get<CompetitionInvitePreview>(`/api/vision/competitions/invite/${code}`),
    join: (code: string) =>
      api.post<CompetitionJoinResponse>(`/api/vision/competitions/join/${code}`),
    approvePlayer: (competitionId: string, playerId: string) =>
      api.post(`/api/vision/competitions/${competitionId}/players/${playerId}/approve`),
    rejectPlayer: (competitionId: string, playerId: string) =>
      api.post(`/api/vision/competitions/${competitionId}/players/${playerId}/reject`),
    removePlayer: (competitionId: string, playerId: string) =>
      api.post(`/api/vision/competitions/${competitionId}/players/${playerId}/remove`),
    promotePlayer: (competitionId: string, playerId: string) =>
      api.post(`/api/vision/competitions/${competitionId}/players/${playerId}/promote`),
    demotePlayer: (competitionId: string, playerId: string) =>
      api.post(`/api/vision/competitions/${competitionId}/players/${playerId}/demote`),
    queuePlayer: (competitionId: string, playerId: string) =>
      api.post(`/api/vision/competitions/${competitionId}/players/${playerId}/queue`),
    unqueuePlayer: (competitionId: string, playerId: string) =>
      api.post(`/api/vision/competitions/${competitionId}/players/${playerId}/unqueue`),
    setPlayerPool: (competitionId: string, playerId: string, pool: 'A' | 'B' | 'C') =>
      api.post(`/api/vision/competitions/${competitionId}/players/${playerId}/pool`, { pool }),
    replacePlayer: (
      competitionId: string,
      teamId: string,
      data: { outPlayerId: string; inPlayerId: string }
    ) => api.post(`/api/vision/competitions/${competitionId}/teams/${teamId}/replace-player`, data),
    removeTeam: (competitionId: string, teamId: string) =>
      api.post(`/api/vision/competitions/${competitionId}/teams/${teamId}/remove`),
    setPlaying: (competitionId: string, playing: boolean) =>
      api.post<{ status: string }>(`/api/vision/competitions/${competitionId}/playing`, { playing }),
    drawTeams: (competitionId: string) =>
      api.post(`/api/vision/competitions/${competitionId}/draw-teams`),
    drawGroups: (competitionId: string, groupSizes?: number[]) =>
      api.post(
        `/api/vision/competitions/${competitionId}/draw-groups`,
        groupSizes ? { groupSizes } : undefined
      ),
    groups: (competitionId: string) =>
      api.get<CompetitionGroupsData>(`/api/vision/competitions/${competitionId}/groups`),
    bracket: (competitionId: string) =>
      api.get<CompetitionBracketData>(`/api/vision/competitions/${competitionId}/bracket`),
    enterResult: (
      competitionId: string,
      matchId: string,
      data: { team1Games: number; team2Games: number; tiebreakScore?: number | null }
    ) => api.post(`/api/vision/competitions/${competitionId}/matches/${matchId}/result`, data),
    wo: (competitionId: string, matchId: string, data: { winnerTeamId: string }) =>
      api.post(`/api/vision/competitions/${competitionId}/matches/${matchId}/wo`, data),
    finish: (competitionId: string) =>
      api.post(`/api/vision/competitions/${competitionId}/finish`),
    cancel: (competitionId: string) =>
      api.post(`/api/vision/competitions/${competitionId}/cancel`),
    delete: (competitionId: string) =>
      fetchWithAuth<{ deleted?: boolean; canceled?: boolean }>(
        `/api/vision/competitions/${competitionId}`,
        { method: 'DELETE' }
      ),
  },
};

// Types (will be moved to types/index.ts)

export interface ProfilePreviewResponse {
  success: boolean;
  profile: {
    name: string;
    username: string;
    avatarUrl: string;
    totalGames: number;
    totalTournaments: number;
  };
  recentActivity: {
    lastMatchDate: string | null;
    lastTournamentName: string | null;
    lastPartner: string | null;
  } | null;
}


